import sweetAlert from 'sweetalert';
import I18n from 'i18n-js/index.js.erb';
import SidePanel from './sidepanel';
import displayDatatablesPagination from '../shared/datatables_pagination';
import { withSaveState } from '../shared/datatables_state';
import {
  overridePaginationClasses,
  customizeDOMPaginationClasses
} from '../../datatables/customization-v2';
import {
  setup as tableSetup,
  setupPaginationOnDOM,
  customizePageLength,
  evaluateSelectedRows,
  defaultTableOptions,
  updateTableItemsCount
} from '../../datatables/table-setup';
import TableRowActive from './table_row_active';

export const LISTS_TABLE_SELECTOR = '.js-items-table';

const ListItemsTable = {
  start() {
    const $listItemsTable = $(`${LISTS_TABLE_SELECTOR}:not(.dataTable)`);
    if (!$listItemsTable.length) {
      return;
    }
    this.startListItemsTable($listItemsTable);
    $listItemsTable.on('datatable:reload', () => $listItemsTable.DataTable().ajax.reload());
    $listItemsTable.on('filters:set', (e, data) => {
      const { filters } = data;
      const _countEnabled = (accumulator, value) => (value.enabled ? accumulator + 1 : accumulator);
      const count = filters.reduce(_countEnabled, 0);
      const $counter = $('.js-active-filters-counter');
      $counter.html(count);
      if (count === 0) {
        $counter.addClass('is-hidden');
      } else {
        $counter.removeClass('is-hidden');
      }
    });
  },

  tearDown() {
    $(`${LISTS_TABLE_SELECTOR}.dataTable`).DataTable().destroy();
  },

  tableColumns(columns) {
    columns[0].render = (data) => {
      const id = data;
      return `
        <input data-id="${id}" id="check_${id}" class="checkbox table__checkbox" type="checkbox">
        <label for="check_${id}" class="table__label js-checkbox-for-row no-space-out voidclick"></label>`;
    };
    columns.forEach((column) => {
      if (column.data === 'tags') {
        column.width = '15%';
        column.createdCell = (cell) => {
          const names = Array.from(cell.querySelectorAll('.tag')).map(s => s.textContent.trim());
          cell.dataset.display = names.join(', ');
        };
      }
      if (column.response_type === 'comment') {
        column.className = 'table__data--truncate has-truncate-text';
      }
    });
    return columns;
  },

  runDelete(result) {
    const table = this;
    if (result) {
      const itemsIds = table
        .rows({ selected: true })
        .data()
        .pluck('id')
        .toArray();
      const deleteUrl = $('.js-delete-items').data('deleteUrl');
      $.ajax({
        url: deleteUrl,
        method: 'DELETE',
        data: { ids: itemsIds }
      }).done((response) => {
        if (response.error) {
          sweetAlert({
            title: 'Something went wrong',
            text: response.error,
            icon: 'error',
            buttons: {
              confirm: {
                text: 'OK',
                value: true,
                visible: true,
                className: '',
                closeModal: true
              }
            }
          });
        }
        table.ajax.reload(null, false);
      });
    }
  },

  watchDelete(table) {
    $('.js-delete-items').click((event) => {
      const text = $(event.currentTarget).data('alertText');
      sweetAlert({
        title: 'Warning!',
        text,
        icon: 'info',
        dangerMode: true,
        buttons: ['Cancel', 'OK']
      }).then(this.runDelete.bind(table));
    });
  },

  watchEdit(table) {
    $('.js-edit-items').click((event) => {
      const $button = $(event.currentTarget);
      const ids = table
        .rows({ selected: true })
        .data()
        .pluck('id')
        .toArray();
      // Single selection → original Edit Person panel (template URL with
      // ITEM_ID placeholder).  Multiple → Bulk Edit People panel.
      if (ids.length === 1) {
        const url = $button.data('singleEditUrl').replace('ITEM_ID', ids[0]);
        SidePanel.loadFromAjax({ url, method: 'get' });
        return;
      }
      const editUrl = $button.data('editUrl');
      SidePanel.show();
      SidePanel.load(editUrl, { list_item_ids: ids, kind: 'details' });
    });
    $(document).on('change', '[name="tag_action"]', function () {
      const needsPicker = this.value === 'add' || this.value === 'remove' || this.value === 'replace';
      $('#js-bulk-tags-picker').toggleClass('is-hidden', !needsPicker);
    });
    SidePanel.onUpdate(() => {
      SidePanel.element().find('.js-bulk-edit-form').on('ajax:error', (event) => {
        const [data, , xhr] = event.detail || [];
        // This form shows the server's own message for permission (403) and
        // stale-selection/validation (422) errors, so keep those from also reaching
        // AjaxErrorHandler's 403 "upgrade your account" alert. Other statuses still
        // propagate, e.g. 401 so an expired session reloads to sign-in.
        if ([403, 422].includes(xhr?.status)) event.stopPropagation();
        sweetAlert({
          title: 'Error',
          text: data?.error || 'The people could not be updated.',
          icon: 'error'
        });
      });
    });
  },

  watchRowActivate() {
    TableRowActive.attach($('.js-items-table').find('tbody'));
  },

  watchDuplicate(table) {
    $('.js-duplicate-items').click((event) => {
      const itemsIds = table
        .rows({ selected: true })
        .data()
        .pluck('id')
        .toArray();
      const duplicateUrl = $(event.currentTarget).data('duplicateUrl');
      $.ajax({
        url: duplicateUrl,
        method: 'POST',
        data: { ids: itemsIds }
      })
        .done(() => {
          table.rows().deselect();
          table.ajax.reload(null, false);
        })
        .fail((response) => {
          const errorMessage = response.responseJSON
            ? response.responseJSON.error
            : response.responseText;
          sweetAlert({ title: 'Error!', text: errorMessage, icon: 'error' });
        });
    });
  },

  startListItemsTable($listItemsTable) {
    // Per-item single-edit URL (with ITEM_ID placeholder) for row-click
    // navigation.  data-edit-url on .js-edit-items now points at the bulk
    // edit endpoint, so we read the single-edit template separately.
    const templateEditUrl = $('.js-edit-items').data('singleEditUrl');
    overridePaginationClasses();
    const $checkAllCheckbox = $('#checkall');
    const itemsName = $listItemsTable.data('plural-entity-name') || 'List Items';
    const selectedTagIds = new Set();

    // Persist the tag filter selection alongside DataTables' built-in state
    // (order, page, search) via the same save/load channel. Listeners must
    // be bound BEFORE .DataTable() is called so the initial stateLoadParams
    // event during init is caught.
    $listItemsTable.on('stateSaveParams.dt', (e, settings, data) => {
      data.selectedTagIds = Array.from(selectedTagIds);
    });
    $listItemsTable.on('stateLoadParams.dt', (e, settings, data) => {
      if (!Array.isArray(data.selectedTagIds) || data.selectedTagIds.length === 0) return;
      selectedTagIds.clear();
      data.selectedTagIds.forEach(id => selectedTagIds.add(String(id)));
      // Restore the action-bar checkboxes (server-rendered and already in the
      // DOM at this point). Use .prop() so no change event fires — we don't
      // want to trigger ajax.reload per box during restore.
      selectedTagIds.forEach(id => {
        $(`.js-tag-filter-check[data-tag-id="${id}"]`).prop('checked', true);
      });
      this._updateTagFilterUI(selectedTagIds.size);
    });

    const table = $listItemsTable.DataTable(withSaveState($listItemsTable, {
      ...defaultTableOptions,
      select: {
        style: 'multi',
        selector: 'label.js-checkbox-for-row',
        className: 'is-selected'
      },
      order: [1, 'asc'],
      columns: this.tableColumns($listItemsTable.data('columns')),
      ajax: {
        url: $listItemsTable.data('source')
      },
      processing: true,
      serverSide: true,
      createdRow(row, data) {
        const itemId = data.id;
        const editUrl = templateEditUrl.replace('ITEM_ID', itemId);
        const $row = $(row);
        $row.addClass('table__row table__row--link');
        $row.click((e) => {
          const isCheckbox = $(e.target).closest('.js-checkbox-row').length > 0;
          if (isCheckbox) {
            e.preventDefault();
            return;
          }
          e.currentTarget.dataset.sidepanelHref = editUrl;
          SidePanel.loadAndShow(e);
        });
      },
      dom: 'rtp',
      drawCallback() {
        customizePageLength(this);
        customizeDOMPaginationClasses();
        evaluateSelectedRows(table, $checkAllCheckbox);
        displayDatatablesPagination(this);
        updateTableItemsCount(this);
      },
      initComplete: () => {
        setupPaginationOnDOM();
        tableSetup(table);
        this.watchDelete(table);
        this.watchDuplicate(table);
        this.watchEdit(table);
        this.watchRowActivate();
        this.watchTagFilter(table, selectedTagIds);
      },
      language: {
        ...defaultTableOptions.language,
        zeroRecords: `<span class="alert alert--info">Sorry, no matching ${itemsName} were found</span>`,
        emptyTable: `<span class="alert alert--info">No ${itemsName} available</span>`
      }
    }));

    $(LISTS_TABLE_SELECTOR).on('preXhr.dt', (e, _settings, data) => {
      const tagIds = $('.js-tag-filter-check:checked').map((_, el) => String($(el).data('tag-id'))).get();
      if (tagIds.length > 0) {
        data.tag_ids = tagIds;
      } else {
        delete data.tag_ids;
      }
    });

    return $listItemsTable;
  },

  watchTagFilter(table, selectedTagIds) {
    $('.js-tag-filter-dropdown .dropdown__list').on('click', (e) => {
      e.stopPropagation();
    });

    $('.js-tag-filter-check').on('change', (e) => {
      const tagId = String($(e.currentTarget).data('tag-id'));
      if (e.currentTarget.checked) {
        selectedTagIds.add(tagId);
      } else {
        selectedTagIds.delete(tagId);
      }
      this._updateTagFilterUI(selectedTagIds.size);
      table.state.save();
      table.ajax.reload();
    });

    $('.js-tag-filter-clear').on('click', (e) => {
      e.preventDefault();
      selectedTagIds.clear();
      $('.js-tag-filter-check').prop('checked', false);
      this._updateTagFilterUI(0);
      table.state.save();
      table.ajax.reload();
    });
  },

  _updateTagFilterUI(count) {
    $('.js-tag-filter-counter').text(count).toggleClass('is-hidden', count === 0);
    $('.js-tag-filter-clear').toggleClass('is-hidden', count === 0);
  }
};

export default ListItemsTable;
